import { useState } from "react";
import { RefreshCw, Wind, Snowflake, Info } from "lucide-react";
import { toast } from "sonner";
import { calcEnv } from "@/lib/api";
import { fmt } from "@/lib/catalog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Section, Field, NumField, Metric } from "./ui";

import { tr } from "@/lib/i18n";
const EXPOSURES = ["I", "II", "III", "IV", "V"];
const SNOW = ["I-A", "I-M", "II", "III"];
const SNOW_LABEL = { "I-A": tr("I - Alpina"), "I-M": tr("I - Mediterranea"), II: "II", III: "III" };

const ZoneSelect = ({ value, onChange, options, label, testid }) => (
  <Select value={value ?? "auto"} onValueChange={(v) => onChange(v === "auto" ? null : v)}>
    <SelectTrigger className="h-11" data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent>
      <SelectItem value="auto">{tr("Automatica")}</SelectItem>
      {options.map((o) => <SelectItem key={o} value={String(o)}>{label(o)}</SelectItem>)}
    </SelectContent>
  </Select>
);

export default function StepEnvironment({ p, update }) {
  const env = p.env || {};
  const [busy, setBusy] = useState(false);

  const calc = async (overrides = {}) => {
    const e = { ...(p.env || {}), ...overrides };
    setBusy(true);
    try {
      update({ env: await calcEnv(p, e) });
    } catch { toast.error(tr("Calcolo dei carichi non riuscito")); }
    setBusy(false);
  };


  const setLoad = (k, v) => update((prev) => ({ env: { ...prev.env, [k]: v, manual: true } }));

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-8 space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4 sr-rise">
        <div>
          <h1 className="text-3xl sm:text-4xl font-display font-bold tracking-tight">{tr("Condizioni ambientali")}</h1>
          <p className="text-slate-500 mt-2">{tr("Zone di vento e neve secondo NTC 2018, ricavate da regione, provincia e altitudine.")}</p>
        </div>
        <Button data-testid="recalc-env-btn" variant="outline" onClick={() => calc()} disabled={busy} className="h-11">
          <RefreshCw className={`w-4 h-4 mr-1.5 ${busy ? "animate-spin" : ""}`} /> {tr("Ricalcola")}
        </Button>
      </div>
      {!p.region && (
        <div className="flex gap-2 items-start p-4 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm" data-testid="env-region-warning">
          <Info className="w-4 h-4 mt-0.5 shrink-0" /> {tr("Regione non impostata: torna al passo 1 o scegli manualmente le zone.")}
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-6">
        <Section kicker={tr("Vento")} title={tr("Azione del vento")}>
          <div className="grid grid-cols-2 gap-4 mb-5">
            <Field label={tr("Zona vento")}>
              <ZoneSelect testid="wind-zone-select" value={env.wind_zone_override} options={[1, 2, 3, 4, 5, 6, 7, 8, 9]} label={(o) => tr("Zona {0}", [o])} onChange={(v) => calc({ wind_zone_override: v })} />
            </Field>
            <Field label={tr("Categoria di esposizione")}>
              <Select value={env.exposure || "II"} onValueChange={(v) => calc({ exposure: v })}>
                <SelectTrigger className="h-11" data-testid="exposure-select"><SelectValue /></SelectTrigger>
                <SelectContent>{EXPOSURES.map((o) => <SelectItem key={o} value={o}>{tr("Categoria")} {o}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          <div className="grid grid-cols-3 gap-3 mb-5">
            <Metric label={tr("Zona")} value={env.wind_zone ?? "–"} testid="wind-zone-value" />
            <Metric label={tr("vb [m/s]")} value={fmt(env.vb)} />
            <Metric label="ce" value={fmt(env.ce)} />
          </div>
          <NumField label={tr("Pressione di picco qp")} unit={tr("kN/m²")} value={env.wind_load} testid="wind-load-input" onChange={(v) => setLoad("wind_load", v)} hint={tr("Valore calcolato, modificabile a mano")} />
          <Wind className="w-24 h-24 text-blue-50 -mb-6 ml-auto" />
        </Section>
        <Section kicker={tr("Neve")} title={tr("Carico da neve")}>
          <div className="grid grid-cols-2 gap-4 mb-5">
            <Field label={tr("Zona neve")}>
              <ZoneSelect testid="snow-zone-select" value={env.snow_zone_override} options={SNOW} label={(o) => tr("Zona {0}", [SNOW_LABEL[o]])} onChange={(v) => calc({ snow_zone_override: v })} />
            </Field>
            <Field label={tr("Altitudine")}><div className="h-11 flex items-center px-3 rounded-md bg-slate-50 border border-slate-200 font-tech text-sm">{fmt(p.altitude, 0)} m</div></Field>
          </div>
          <div className="grid grid-cols-3 gap-3 mb-5">
            <Metric label={tr("Zona")} value={env.snow_zone ? SNOW_LABEL[env.snow_zone] : "–"} testid="snow-zone-value" />
            <Metric label={tr("qsk [kN/m²]")} value={fmt(env.qsk)} />
            <Metric label="μ1" value={fmt(env.mu)} />
          </div>
          <NumField label={tr("Carico neve sulla copertura qs")} unit={tr("kN/m²")} value={env.snow_load} testid="snow-load-input" onChange={(v) => setLoad("snow_load", v)} hint={tr("Valore per copertura piana (μ1 = 0,8). Per ogni falda il μ1 è ricalcolato in base alla pendenza.")} />
          <Snowflake className="w-24 h-24 text-blue-50 -mb-6 ml-auto" />
        </Section>
      </div>
      <p className="text-xs text-slate-500 flex gap-1.5"><Info className="w-3.5 h-3.5 shrink-0" />{tr("Calcolo semplificato e indicativo: la zona neve è stimata a livello regionale. Verifica sempre con la normativa e la provincia esatta.")}</p>
    </div>
  );
}
